import React from 'react';
import { css } from '@emotion/css';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldOptionModel from '../../../models/templating/api/TemplateFormFieldOptionModel';
import TemplateFieldType from '../../../models/templating/TemplateFieldType';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import { color_text_light, color_shades_dark } from '../../../constants/colors';
import generateGuid from '../../../services/guids';

export type FieldTypeFamily = 'text' | 'options' | 'special';

export interface FieldTypeOption {
    value: TemplateFieldType
    label: string
    family: FieldTypeFamily
}

export const FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
    { value: 'ShortTextField', label: 'Short Text Field', family: 'text' },
    { value: 'LongTextField', label: 'Long Text Field', family: 'text' },
    { value: 'SSMLTextField', label: 'SSML Field', family: 'text' },
    { value: 'NumberTextField', label: 'Number Field', family: 'text' },
    { value: 'DateTextField', label: 'Date Field', family: 'text' },
    { value: 'PhoneNumberField', label: 'Phone Number Field', family: 'text' },
    { value: 'SelectField', label: 'Dropdown', family: 'options' },
    { value: 'RadioGroupField', label: 'Radio Buttons', family: 'options' },
    { value: 'CheckboxGroupField', label: 'Checkboxes', family: 'options' },
    { value: 'FileUploadField', label: 'File Upload', family: 'special' },
    { value: 'SingleDayTimeRangeField', label: 'Single Day Hours', family: 'special' },
    { value: 'SpecialDateField', label: 'Special Dates & Holidays', family: 'special' },
    { value: 'TimeRangeField', label: 'Weekly Hours', family: 'special' },
];

export const getFieldFamily = (fieldType: string): FieldTypeFamily => {
    const option = FIELD_TYPE_OPTIONS.find(o => o.value === fieldType);
    return option?.family ?? 'text';
};

/**
 * Detects whether a text field's defaultValue looks like a boolean sentinel
 * (exactly "true" or "false", case-insensitive).
 */
const isBooleanDefault = (value: string | undefined): boolean => {
    const v = (value ?? '').trim().toLowerCase();
    return v === 'true' || v === 'false';
};

/**
 * Builds the standard true/false option pair used for boolean RadioGroup / Select fields.
 */
const buildBooleanOptions = (fieldId: string): TemplateFormFieldOptionModel[] => [
    { id: generateGuid(), templateFormFieldId: fieldId, label: 'True', value: 'true', priority: 0 },
    { id: generateGuid(), templateFormFieldId: fieldId, label: 'False', value: 'false', priority: 1 },
];

/**
 * Returns the updated field after a type change, stripping incompatible data.
 * Preserves: id, templateFormSectionId, associatedVariable, title, label, tip,
 *            isRequired, placeholder, priority, conditionalDefaults, instructionsMarkdown
 *
 * Auto-populate behaviour when switching text → options:
 *  - If the existing defaultValue is "true" or "false" (boolean sentinel),
 *    pre-populate two options ("True"/"False") and keep the defaultValue.
 *  - Otherwise, start with empty options and clear defaultValue.
 */
export const applyFieldTypeChange = (
    field: TemplateFormFieldModel,
    newType: TemplateFieldType
): TemplateFormFieldModel => {
    const fromFamily = getFieldFamily(field.fieldType);
    const toFamily = getFieldFamily(newType);

    const base: TemplateFormFieldModel = {
        id: field.id,
        templateFormSectionId: field.templateFormSectionId,
        fieldType: newType,
        associatedVariable: field.associatedVariable,
        title: field.title,
        label: field.label,
        tip: field.tip,
        isRequired: field.isRequired,
        placeholder: field.placeholder,
        priority: field.priority,
        conditionalDefaults: field.conditionalDefaults,
        instructionsMarkdown: field.instructionsMarkdown,
        name: field.name,
    };

    if (toFamily === 'options') {
        if (fromFamily === 'options') {
            // Preserve existing options and defaultValue when staying in the options family
            base.options = field.options ?? [];
            base.defaultValue = field.defaultValue;
        } else {
            // Switching from text/special → options
            // Auto-populate true/false options when the current default looks boolean
            if (isBooleanDefault(field.defaultValue)) {
                base.options = buildBooleanOptions(field.id);
                base.defaultValue = (field.defaultValue ?? '').trim().toLowerCase();
            } else {
                base.options = [];
                base.defaultValue = '';
            }
        }
    } else if (toFamily === 'text') {
        // Text family: preserve defaultValue if coming from text
        base.defaultValue = fromFamily === 'text' ? field.defaultValue : '';
        // No options on text fields
    } else {
        // Special family: preserve defaultValue
        base.defaultValue = field.defaultValue;
    }

    return base;
};

/**
 * Returns true if changing from `fromType` to `toType` will cause data loss
 * that the user should be warned about.
 */
export const isDestructiveTypeChange = (fromType: string, toType: TemplateFieldType): boolean => {
    const fromFamily = getFieldFamily(fromType);
    const toFamily = getFieldFamily(toType);
    if (fromFamily === toFamily) return false;
    // Switching families is destructive (options cleared, or defaultValue cleared)
    return true;
};

interface FieldTypeChangeSelectorProps {
    field: TemplateFormFieldModel
    onChange: (updatedField: TemplateFormFieldModel) => void
    /** Called after a type change is applied so the parent editor can ensure the field is expanded. */
    onExpand?: () => void
    /** If true, only show non-special types (for special field editors that allow changing away) */
    excludeSpecial?: boolean
}

interface FieldTypeChangeSelectorState {
    pendingType: TemplateFieldType | null
    isConfirmOpen: boolean
}

class FieldTypeChangeSelector extends React.Component<FieldTypeChangeSelectorProps, FieldTypeChangeSelectorState> {
    constructor(props) {
        super(props);
        this.state = {
            pendingType: null,
            isConfirmOpen: false
        };
    }

    handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as TemplateFieldType;
        if (newType === this.props.field.fieldType) return;

        if (isDestructiveTypeChange(this.props.field.fieldType, newType)) {
            this.setState({ pendingType: newType, isConfirmOpen: true });
        } else {
            this.applyChange(newType);
        }
    }

    applyChange = (newType: TemplateFieldType) => {
        const updatedField = applyFieldTypeChange(this.props.field, newType);
        this.props.onChange(updatedField);
        this.setState({ pendingType: null, isConfirmOpen: false });
        if (this.props.onExpand) {
            this.props.onExpand();
        }
    }

    handleConfirm = () => {
        if (this.state.pendingType) {
            this.applyChange(this.state.pendingType);
        }
    }

    handleCancel = () => {
        this.setState({ pendingType: null, isConfirmOpen: false });
    }

    getWarningMessage(): string {
        const fromFamily = getFieldFamily(this.props.field.fieldType);
        const toFamily = this.state.pendingType ? getFieldFamily(this.state.pendingType) : '';
        const pendingLabel = FIELD_TYPE_OPTIONS.find(o => o.value === this.state.pendingType)?.label ?? this.state.pendingType;

        if (fromFamily === 'options' && toFamily === 'text') {
            return `Changing to "${pendingLabel}" will remove all configured options from this field. The field's title, label, description, and associated variable will be preserved.`;
        }
        if (fromFamily === 'text' && toFamily === 'options') {
            return `Changing to "${pendingLabel}" will clear the default value. You will need to add options to the field. The field's title, label, description, and associated variable will be preserved.`;
        }
        if (fromFamily === 'special') {
            return `Changing to "${pendingLabel}" will remove special field configuration. The field's title, label, description, and associated variable will be preserved.`;
        }
        if (toFamily === 'special') {
            return `Changing to "${pendingLabel}" will clear existing field configuration. The field's title, label, description, and associated variable will be preserved.`;
        }
        return `Changing the field type to "${pendingLabel}" may result in some configuration being cleared.`;
    }

    render() {
        const { field, excludeSpecial } = this.props;
        const availableOptions = excludeSpecial
            ? FIELD_TYPE_OPTIONS.filter(o => o.family !== 'special')
            : FIELD_TYPE_OPTIONS;

        return (
            <>
                <div className={selectorWrapperStyle} onClick={e => e.stopPropagation()}>
                    <select
                        className={selectStyle}
                        value={field.fieldType}
                        onChange={this.handleSelectChange}
                        title="Change field type"
                    >
                        {availableOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                {this.state.isConfirmOpen && (
                    <ConfirmationDialog
                        title="Change Field Type"
                        deleteText="Yes, Change Type"
                        isLoading={false}
                        onClose={this.handleCancel}
                        onConfirm={this.handleConfirm}
                    >
                        <div className={confirmBodyStyle}>
                            <p>{this.getWarningMessage()}</p>
                            <p className="note">This only affects how the field is displayed in the form. Existing saved values in child modules are unaffected and remain linked by the field's ID.</p>
                        </div>
                    </ConfirmationDialog>
                )}
            </>
        );
    }
}

const selectorWrapperStyle = css`
    display: inline-flex;
    align-items: center;
`;

const selectStyle = css`
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    color: ${color_text_light};
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 2px 4px;
    cursor: pointer;
    appearance: auto;
    max-width: 160px;
    &:hover {
        border-color: ${color_shades_dark};
        background: white;
    }
    &:focus {
        outline: none;
        border-color: ${color_shades_dark};
        background: white;
    }
`;

const confirmBodyStyle = css`
    padding: 8px 32px;
    p {
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 12px;
    }
    .note {
        font-size: 12px;
        color: ${color_text_light};
        font-style: italic;
        margin-bottom: 0;
    }
`;

export default FieldTypeChangeSelector;
